import { useRef, useState } from "react";
import {
  motion,
  type MotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import { whyUsCards } from "@/config/why-us.config";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import SegmentedProgress from "../ui/segmented-progress";
import { whyUsProofSummaries } from "../ui/why-us-summary";
import { cn } from "@/lib/utils";

interface ReasonSceneProps {
  activeReason: number;
  index: number;
  motionDisabled: boolean;
  progress: MotionValue<number>;
}

function ReasonScene({
  activeReason,
  index,
  motionDisabled,
  progress,
}: ReasonSceneProps) {
  const card = whyUsCards[index];
  const opacityStops =
    index === 0
      ? [0, 0.24, 0.36]
      : index === whyUsCards.length - 1
        ? [0.64, 0.76, 1]
        : [0.27, 0.39, 0.61, 0.73];
  const opacityValues =
    index === 0
      ? [1, 1, 0]
      : index === whyUsCards.length - 1
        ? [0, 1, 1]
        : [0, 1, 1, 0];
  const transformStops =
    index === 0
      ? [0, 0.24, 0.36]
      : index === whyUsCards.length - 1
        ? [0.64, 0.76, 1]
        : [0.27, 0.39, 0.61, 0.73];
  const transformValues =
    index === 0
      ? [
          "translate3d(0, 0px, 0) scale(1)",
          "translate3d(0, 0px, 0) scale(1)",
          "translate3d(0, -42px, 0) scale(0.985)",
        ]
      : index === whyUsCards.length - 1
        ? [
            "translate3d(0, 42px, 0) scale(0.985)",
            "translate3d(0, 0px, 0) scale(1)",
            "translate3d(0, 0px, 0) scale(1)",
          ]
        : [
            "translate3d(0, 42px, 0) scale(0.985)",
            "translate3d(0, 0px, 0) scale(1)",
            "translate3d(0, 0px, 0) scale(1)",
            "translate3d(0, -42px, 0) scale(0.985)",
          ];
  const opacity = useTransform(progress, opacityStops, opacityValues);
  const transform = useTransform(progress, transformStops, transformValues);
  // Out-of-focus in, sharp while held, out-of-focus out: a rack focus.
  const filter = useTransform(
    progress,
    opacityStops,
    opacityValues.map((value) => `blur(${(1 - value) * 12}px)`)
  );
  // Numerals drift slower than the copy for a sense of depth.
  const numeralY = useTransform(progress, [0, 1], [90, -90]);

  return (
    <motion.div
      aria-hidden="true"
      style={motionDisabled ? undefined : { opacity, transform, filter }}
      className={`absolute inset-0 flex items-center justify-center px-8 pb-28 pt-32 lg:px-12 ${
        motionDisabled && index !== activeReason ? "hidden" : ""
      }`}
    >
      <motion.span
        aria-hidden="true"
        style={motionDisabled ? undefined : { y: numeralY }}
        className="text-outline pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none text-[clamp(14rem,30vw,26rem)] font-medium leading-none tracking-[-0.06em] opacity-40"
      >
        {String(index + 1).padStart(2, "0")}
      </motion.span>
      <div className="relative mx-auto w-full max-w-5xl text-center">
        <span
          className="mx-auto mb-10 block h-px w-16 bg-linear-to-r from-[#149CFF] to-[#DEFF77]"
          aria-hidden="true"
        />
        <h2 className="text-[clamp(1.9rem,4vw,3.5rem)] font-light leading-[1.02] tracking-[-0.04em]">
          {card!.title}
        </h2>
        <p className="mx-auto mt-10 max-w-3xl text-[clamp(0.95rem,1.35vw,1.2rem)] font-light leading-[1.75] text-white/62">
          {whyUsProofSummaries[index]}
        </p>
      </div>
    </motion.div>
  );
}

export default function DesktopWhyUs() {
  const storyRef = useRef<HTMLElement>(null);
  const [activeReason, setActiveReason] = useState(0);
  const reduceMotion = useReducedMotion();
  const hasHardwareAcceleration = useHardwareAcceleration();
  const motionDisabled = Boolean(reduceMotion) || !hasHardwareAcceleration;
  const { scrollYProgress } = useScroll({
    target: storyRef,
    offset: ["start start", "end end"],
  });
  const chapterLight = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [
      "radial-gradient(circle at 50% 47%, rgba(20,156,255,0.14), transparent 40%)",
      "radial-gradient(circle at 50% 47%, rgba(40,200,215,0.13), transparent 40%)",
      "radial-gradient(circle at 50% 47%, rgba(190,235,110,0.1), transparent 40%)",
    ]
  );

  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const nextReason = Math.min(
      whyUsCards.length - 1,
      Math.floor(Math.min(0.999, Math.max(0, progress)) * whyUsCards.length)
    );

    setActiveReason((current) =>
      current === nextReason ? current : nextReason
    );
  });

  return (
    <section
      id="why-us"
      ref={storyRef}
      data-desktop-section="why-us"
      className="relative min-h-[320vh]"
    >
      <div className="sticky top-0 h-dvh overflow-hidden">
        <motion.div
          className="pointer-events-none absolute inset-0"
          style={{ background: chapterLight }}
          aria-hidden="true"
        />

        {whyUsCards.map((card, index) => (
          <ReasonScene
            key={card.title}
            activeReason={activeReason}
            index={index}
            motionDisabled={motionDisabled}
            progress={scrollYProgress}
          />
        ))}

        <div className="absolute inset-x-8 bottom-9 mx-auto flex max-w-6xl items-center gap-5 lg:inset-x-12">
          <span className="min-w-20 font-mono text-xs text-white/48">
            {String(activeReason + 1).padStart(2, "0")} /{" "}
            {String(whyUsCards.length).padStart(2, "0")}
          </span>
          <SegmentedProgress
            activeIndex={activeReason}
            count={whyUsCards.length}
            label="Why Us chapter progress"
            progress={scrollYProgress}
          />
          <div className="flex min-w-28 items-center justify-end gap-2 text-xs text-white/50">
            <span>Keep scrolling</span>
            <span
              className={cn(
                "text-base leading-none text-white/65",
                !motionDisabled && "nudge-down"
              )}
              aria-hidden="true"
            >
              ↓
            </span>
          </div>
        </div>
      </div>

      <ol className="sr-only">
        {whyUsCards.map((card, index) => (
          <li key={card.title}>
            <h2>{card.title}</h2>
            <p>{whyUsProofSummaries[index]}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
